import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { recordConsent, declineConsent } from '../lib/creditProvider';

export default function CreditConsent({ user }) {
  const { requestId } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login?next=/credit/consent/' + requestId); return; }
    (async () => {
      const { data, error } = await supabase
        .from('credit_report_requests')
        .select('*')
        .eq('id', requestId)
        .maybeSingle();
      if (error) setErr(error.message);
      setRequest(data || null);
      setLoading(false);
    })();
  }, [requestId, user, navigate]);

  const handleAuthorize = async () => {
    setSubmitting(true); setErr(null);
    try {
      const updated = await recordConsent({ requestId });
      setRequest(updated);
    } catch (e) { setErr(e.message); }
    setSubmitting(false);
  };

  const handleDecline = async () => {
    if (!window.confirm('Decline this request? The property management will not receive a credit report.')) return;
    setSubmitting(true); setErr(null);
    try {
      const updated = await declineConsent({ requestId });
      setRequest(updated);
    } catch (e) { setErr(e.message); }
    setSubmitting(false);
  };

  if (loading) return <Wrap><p style={muted}>Loading…</p></Wrap>;
  if (!request) return <Wrap><p style={muted}>This request was not found or you don't have access to it.</p></Wrap>;

  if (request.tenant_id !== user.id) {
    return <Wrap>
      <h1 style={h1}>Not your request</h1>
      <p style={muted}>This consent link is for a different account. Sign in as the tenant the request was sent to.</p>
    </Wrap>;
  }

  if (request.status === 'ready' || request.status === 'consented') {
    return <Wrap>
      <div style={successBadge}>✓ Authorized</div>
      <h1 style={h1}>Thanks — you're done.</h1>
      <p style={muted}>
        Your credit report has been shared with <b>{request.landlord_name || 'the property management'}</b>.
        You can close this page.
      </p>
    </Wrap>;
  }
  if (request.status === 'declined') {
    return <Wrap>
      <div style={declinedBadge}>Declined</div>
      <h1 style={h1}>Request declined.</h1>
      <p style={muted}>The property management will not receive a credit report.</p>
    </Wrap>;
  }

  return (
    <Wrap>
      <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Credit report request</div>
      <h1 style={h1}>{request.landlord_name || 'A property management'} is requesting your credit report.</h1>
      <p style={lede}>
        They've paid for a screening report as part of evaluating you as a tenant.
        Nothing is pulled until you authorize it below.
      </p>

      <div style={panel}>
        <Disclosure label="What will be shared">
          A standard tenant-screening credit report: credit score, open accounts,
          balances, payment history, public records, and any eviction filings.
          No bank passwords. No tax returns.
        </Disclosure>
        <Disclosure label="Who will see it">
          Only <b>{request.landlord_name || 'the requesting property management'}</b>. RenterRep does not sell or
          share credit data with anyone else.
        </Disclosure>
        <Disclosure label="Soft vs. hard inquiry">
          Soft inquiry. This will not affect your credit score.
        </Disclosure>
        <Disclosure label="Your rights (FCRA)">
          You have the right to a free copy of any report we pull on you, and to
          dispute inaccurate information directly with the credit bureau.
        </Disclosure>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '20px 0 16px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={e => setAcknowledged(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.5 }}>
          I authorize RenterRep and its screening partner to pull my credit
          report and share it with <b>{request.landlord_name || 'the requesting property management'}</b>. I confirm I am
          the person named on this request.
        </span>
      </label>

      {err && <div style={errStyle}>⚠️ {err}</div>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={handleAuthorize}
          disabled={!acknowledged || submitting}
          style={{ ...btnPrimary, opacity: (!acknowledged || submitting) ? 0.5 : 1, cursor: (!acknowledged || submitting) ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Authorizing…' : 'I authorize'}
        </button>
        <button onClick={handleDecline} disabled={submitting} style={btnDanger}>
          Decline
        </button>
      </div>

      <p style={{ ...muted, fontSize: 11, marginTop: 18 }}>
        SAMPLE / PRE-LAUNCH: while RenterRep is in launch phase, no real bureau
        pull occurs — your authorization records consent and shares a sample
        report so the workflow can be tested end-to-end.
      </p>
    </Wrap>
  );
}

function Disclosure({ label, children }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid rgba(184,137,58,0.18)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2C4E', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Wrap({ children }) {
  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 32, paddingBottom: 48, maxWidth: 640 }}>
        <div style={{ background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(120,90,30,0.06)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const h1 = { fontFamily: "'DM Serif Display', serif", fontSize: 24, fontWeight: 400, color: '#0F2C4E', marginBottom: 8, lineHeight: 1.25 };
const lede = { fontSize: 14, color: '#2C2C2C', lineHeight: 1.6, marginBottom: 16 };
const muted = { fontSize: 13, color: '#6B6358', lineHeight: 1.6 };
const panel = { background: '#F5EFE3', border: '1px solid rgba(184,137,58,0.18)', borderRadius: 12, padding: '4px 14px' };
const btnPrimary = { background: '#0F2C4E', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 50, fontSize: 14, fontWeight: 700 };
const btnDanger = { background: 'transparent', color: '#C0392B', border: '1.5px solid #C0392B', padding: '12px 24px', borderRadius: 50, fontSize: 14, fontWeight: 700, cursor: 'pointer' };
const errStyle = { background: '#FDEDEB', border: '1px solid #C0392B', color: '#C0392B', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 10 };
const successBadge = { display: 'inline-block', background: '#E8F5EE', color: '#1B7A4B', border: '1px solid #1B7A4B', padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 12 };
const declinedBadge = { display: 'inline-block', background: '#FDEDEB', color: '#C0392B', border: '1px solid #C0392B', padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 12 };
